import React from "react";

import { DateOptions } from "./constants";

import Filter from "../Filter/Filter";

import CalendarIcon from "../../assets/Icons/CalendarIcon.svg?react";
import DatePicker, { registerLocale } from "react-datepicker";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import "dayjs/locale/ru";

import { ru as dateFnsRu } from "date-fns/locale/ru";
import { enGB as dateFnsEn } from "date-fns/locale/en-GB";

registerLocale("ru", dateFnsRu);
registerLocale("en", dateFnsEn);

export type CustomDateFilterValue = {
  fromDate: string;
  toDate: string;
};

type Props = {
  value?: number;
  customValue?: CustomDateFilterValue;
  onChange: (value?: number, customValue?: CustomDateFilterValue) => void;
};

const DateFilter = React.memo(({ onChange, value, customValue }: Props) => {
  const { t, i18n } = useTranslation("translation");
  const [lang, setLang] = React.useState(i18n.language);

  React.useEffect(() => {
    setLang(i18n.language);
  }, [i18n.language]);

  const [dateRange, setDateRange] = React.useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, endDate] = dateRange;

  React.useEffect(() => {
    if (!customValue) {
      setDateRange([null, null]);
    } else {
      setDateRange([
        new Date(customValue.fromDate),
        new Date(customValue.toDate),
      ]);
    }
  }, [customValue]);

  const handleChange = React.useCallback(
    (id?: number) => {
      onChange(id);
    },
    [onChange]
  );

  const handleChangeCustomDateFilter = React.useCallback(
    (value: [Date | null, Date | null]) => {
      setDateRange(value);
    },
    []
  );

  const handleBlurCustomDateFilter = React.useCallback(() => {
    if (startDate === null && endDate === null) {
      return;
    }

    if (endDate === null) {
      setDateRange([null, null]);
      return;
    }

    onChange(undefined, {
      fromDate: dayjs(startDate).format("YYYY-MM-DD"),
      toDate: dayjs(endDate).format("YYYY-MM-DD"),
    });
  }, [startDate, endDate, onChange]);

  return (
    <Filter
      icon={<CalendarIcon />}
      label={t("dates")}
      options={DateOptions}
      value={value}
      onChange={handleChange}
      additionalContent={
        <div className="StatsHeader__DatePicker">
          <DatePicker
            locale={lang}
            placeholderText={t("select_date")}
            showPopperArrow={false}
            selectsRange
            startDate={startDate!}
            endDate={endDate!}
            onChange={handleChangeCustomDateFilter}
            onCalendarClose={handleBlurCustomDateFilter}
            disabledKeyboardNavigation
            onKeyDown={e => {
              e.preventDefault();
            }}
            excludeDateIntervals={[
              {
                start: new Date(),
                end: new Date("2030-01-01"),
              },
            ]}
          />
        </div>
      }
      allSelectedFunction={() =>
        value == undefined && startDate === null && endDate === null
      }
    />
  );
});

export default DateFilter;
